import type { FilterLookups } from "../types";

export type BuildCompanyFilterLookupsInput = {
  countries: Array<{ alpha2?: string | null; name?: string | null }>;
  states: Array<{ id?: unknown; name?: unknown }>;
  hgIndustries: Array<{ id: string | number; name: string }>;
  sic: Array<{
    id: string;
    name: string;
    code?: string | null;
    parentId?: string | null;
  }>;
  naics: Array<{
    id: string;
    name: string;
    code?: string | null;
    parentId?: string | null;
  }>;
  products: Array<{ id: string | number; name: string }>;
  vendors: Array<{ id: string | number; name: string }>;
  productCategories: Array<{
    id?: string | null;
    name?: string | null;
    parentId?: string | null;
  }>;
  productAttributes: Array<{
    id: string | number;
    name: string;
    childrenAttributes?: Array<{ id: string | number; name: string }> | null;
  }>;
  spendCategories: Array<{
    id: string | number;
    name: string;
    nameTree?: string[] | null;
  }>;
  buyersJourneys?: Array<{ id: string; name: string }>;
  contextTypes?: Array<{ id: string; name: string }>;
  /** V2 scoring models (argo catalog) — resolves model-score chip names. */
  scoringModels?: Array<{ family: string; name: string }>;
};

export function buildCompanyFilterLookups({
  countries,
  states,
  hgIndustries,
  sic,
  naics,
  products,
  vendors,
  productCategories,
  productAttributes,
  spendCategories,
  buyersJourneys,
  contextTypes,
  scoringModels,
}: BuildCompanyFilterLookupsInput): FilterLookups {
  const geographyLookups = {
    countries: countries
      .filter((c): c is typeof c & { alpha2: string; name: string } =>
        Boolean(c.alpha2 && c.name)
      )
      .map((c) => ({ id: c.alpha2, name: c.name })),
    states: states
      .filter(
        (s): s is typeof s & { id: unknown; name: string } =>
          s.id !== undefined && s.name !== undefined
      )
      .map((s) => ({ id: String(s.id), name: s.name })),
  };

  return {
    targetOrganizations: undefined,
    uploadedAccounts: undefined,
    knownCompanies: undefined,
    companyType: undefined,
    employees: undefined,
    revenue: undefined,
    globalPresence: undefined,
    rankings: undefined,
    promotionDate: undefined,
    geography: geographyLookups,
    industry: {
      industries: hgIndustries.map((i) => ({ id: i.id, name: i.name })),
      sicCodes: sic.map((s) => {
        const { code } = s;
        return {
          id: s.id,
          name: code ? `${code}: ${s.name}` : s.name,
          parentId: s.parentId,
        };
      }),
      naicsCodes: naics.map((n) => ({
        id: n.id,
        name: n.code ? `${n.code}: ${n.name}` : n.name,
        parentId: n.parentId,
      })),
    },
    products: {
      products: products.map((p) => ({ id: p.id, name: p.name })),
    },
    vendors: {
      vendors: vendors.map((v) => ({ id: v.id, name: v.name })),
    },
    productCategories: {
      categories: productCategories
        .filter(
          (c): c is typeof c & { id: string; name: string } =>
            c.id !== undefined && c.name !== undefined
        )
        .map((c) => ({ id: c.id, name: c.name, parentId: c.parentId })),
    },
    productAttributes: {
      attributes: productAttributes.flatMap((a) => [
        { id: a.id, name: a.name },
        ...(a.childrenAttributes ?? []).map((child) => ({
          id: child.id,
          name: child.name,
        })),
      ]),
    },
    itSpend: {
      spendCategories: spendCategories.map((c) => ({
        id: c.id,
        name: c.nameTree?.join(" > ") ?? c.name,
      })),
    },
    intent:
      buyersJourneys || contextTypes
        ? {
            buyersJourneys:
              buyersJourneys?.map((j) => ({
                id: j.id,
                name: j.name,
              })) ?? [],
            contextTypes:
              contextTypes?.map((c) => ({
                id: c.id,
                name: c.name,
              })) ?? [],
            // Used by Intent chips tooltips (display only)
            countries: countries
              .filter((c): c is typeof c & { alpha2: string; name: string } =>
                Boolean(c.alpha2 && c.name)
              )
              .map((c) => ({ id: c.alpha2, code: c.alpha2, name: c.name })),
            states: states
              .filter(
                (s): s is typeof s & { id: unknown; name: string } =>
                  s.id !== undefined && s.name !== undefined
              )
              .map((s) => ({ id: String(s.id), name: s.name })),
          }
        : undefined,
    fitScore: undefined,
    intentScore: undefined,
    contactMode: undefined,
    contactJobSeniority: undefined,
    contactJobFunction: undefined,
    contactLocation: geographyLookups,
    contactJobTitle: undefined,
    contactTimeInRole: undefined,
    buyerIntentProducts: undefined,
    buyerIntentVendors: undefined,
    buyerIntentCategories: undefined,
    buyerIntentAttributes: undefined,
    companyConfig: undefined,
    financingEvents: undefined,
    modelScore: scoringModels
      ? {
          models: scoringModels.map((m) => ({ id: m.family, name: m.name })),
        }
      : undefined,
  };
}
